<template>
  <b-modal
    ref="modal"
    title="Manual Result Bet"
    centered
    size="md"
    hide-footer
    @hide="hideModal"
  >
    <div v-if="bet" style="color: #1a1a1a">
      <!-- Bet header -->
      <div style="font-size: 12px; color: #6b7280; margin-bottom: 2px">
        Betslip:
        <strong style="color: #1a1a1a">{{ bet.displayId || bet.id }}</strong>
      </div>
      <div style="font-size: 12px; color: #6b7280; margin-bottom: 2px">
        Type: <strong style="color: #1a1a1a">{{ typeLabel || bet.type }}</strong>
        &nbsp; {{ isBonus ? "Bonus" : "Stake" }}:
        <strong style="color: #1a1a1a">{{ formatCurrencyBuiltInFunc(bet.stake || 0) }}</strong>
        <template v-if="isPerComboPayout">
          &nbsp; Per Combo:
          <strong style="color: #1a1a1a">{{ formatCurrencyBuiltInFunc(perComboStake) }}</strong>
        </template>
      </div>
      <div style="font-size: 12px; color: #6b7280; margin-bottom: 14px">
        Current: <strong style="color: #1a1a1a">{{ bet.status }} / {{ bet.result || "-" }}</strong>
        <template v-if="bet.totalPayout">
          &nbsp; Paid:
          <strong style="color: #1a1a1a">{{ formatCurrencyBuiltInFunc(bet.totalPayout) }}</strong>
        </template>
      </div>

      <!-- Bet result (exotic / simple) -->
      <template v-if="mode === MODES.EXOTIC || mode === MODES.SIMPLE">
        <div :style="sectionTitleStyle">Bet Result</div>
        <b-form-radio-group
          v-model="betResult"
          :options="betResultOptions"
          name="manual-bet-result"
          @change="onBetResultChange"
          style="margin-bottom: 16px; font-size: 13px"
        />
      </template>

      <!-- Legs (single / multi) -->
      <template v-if="mode === MODES.LEG">
        <div :style="sectionTitleStyle">Legs</div>
        <div
          v-for="(leg, idx) in legs"
          :key="leg.legId"
          :style="legRowStyle(idx)"
        >
          <div style="font-size: 12px; color: #374151; margin-bottom: 6px">
            {{ idx + 1 }}. {{ leg.label }}
            <b-badge v-if="leg.result !== leg.initialResult" variant="light-warning" style="font-size: 10px">
              changed
            </b-badge>
          </div>
          <b-form-radio-group
            v-model="leg.result"
            :options="legResultOptions"
            :name="`manual-leg-result-${idx}`"
            style="font-size: 13px"
          />
        </div>
      </template>

      <!-- Runners (quaddie / daily double / running double / treble) -->
      <template v-if="mode === MODES.QUADDIE">
        <div :style="sectionTitleStyle">Runners</div>
        <div
          v-for="(leg, idx) in legs"
          :key="leg.legId"
          :style="legRowStyle(idx)"
        >
          <div style="font-size: 12px; color: #374151; margin-bottom: 6px">
            {{ leg.label }}
            <b-badge v-if="leg.scratched" variant="light-secondary" style="font-size: 10px">
              Scratched
            </b-badge>
            <b-badge v-if="leg.result !== leg.initialResult" variant="light-warning" style="font-size: 10px">
              changed
            </b-badge>
          </div>
          <div
            v-if="leg.scratched && leg.result !== 'cancel'"
            class="text-warning"
            style="font-size: 11px; margin-bottom: 4px"
          >
            Runner is scratched but still active, consider Void.
          </div>
          <b-form-radio-group
            v-model="leg.result"
            :options="runnerResultOptions"
            :name="`manual-runner-result-${idx}`"
            style="font-size: 13px"
          />
        </div>
      </template>

      <!-- Runner positions (exotic, display only) -->
      <template v-if="mode === MODES.EXOTIC">
        <div :style="sectionTitleStyle">Runners</div>
        <div style="font-size: 11px; color: #6b7280; margin-bottom: 8px">
          <strong>Void</strong>: remove the runner like a scratching. Affects payout:
          combos and stake per combo are recalculated from the remaining runners,
          and the bet is refunded if no valid combo is left.<br />
          <strong>Finish Pos.</strong>: position the runner finished in the race
          (1 = winner, 2 = second, ...), display only. Leave blank if unknown or to clear it.
        </div>
        <div
          class="d-flex align-items-center"
          style="gap: 8px; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; padding-bottom: 4px"
        >
          <span style="flex: 1">Runner</span>
          <span style="width: 40px; text-align: center">Void</span>
          <span style="width: 80px; text-align: center">Finish Pos.</span>
        </div>
        <div
          v-for="leg in legs"
          :key="leg.legId"
          class="d-flex align-items-center"
          style="gap: 8px; padding: 3px 0"
        >
          <span style="flex: 1; font-size: 12px; color: #374151">
            {{ leg.label }}
            <b-badge v-if="leg.scratched" variant="light-secondary" style="font-size: 10px">
              Scratched
            </b-badge>
            <div
              v-if="leg.scratched && !leg.cancelled"
              class="text-warning"
              style="font-size: 11px"
            >
              Scratched but still active, consider Void.
            </div>
          </span>
          <div style="width: 40px; flex-shrink: 0" class="d-flex justify-content-center">
            <b-form-checkbox v-model="leg.cancelled" />
          </div>
          <b-form-input
            v-model="leg.finalPosition"
            type="text"
            inputmode="numeric"
            :formatter="formatPositionInput"
            size="sm"
            placeholder="-"
            style="width: 80px; flex-shrink: 0"
          />
        </div>
      </template>

      <!-- Final odds / dividend -->
      <div :style="sectionTitleStyle" class="mt-1">{{ oddsLabel }}</div>
      <b-form-input
        v-model="finalOdds"
        type="text"
        inputmode="decimal"
        :formatter="formatOddsInput"
        :placeholder="isFinalOddsRequired ? 'Required' : 'Not required for this result'"
        :disabled="!isFinalOddsRequired"
        :state="isFinalOddsRequired && finalOdds !== '' ? isValidFinalOdds(finalOdds) : null"
      />
      <small class="text-muted">{{ oddsHint }}</small>

      <!-- Summary -->
      <div class="bg-light p-1 mt-1 rounded" style="font-size: 13px">
        <div class="d-flex justify-content-between">
          <span class="text-muted">New bet result:</span>
          <strong>{{ resultText(expectedResult) }}</strong>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">Estimated payout (exact amount shown on Save):</span>
          <strong>{{ estimatedPayoutText }}</strong>
        </div>
        <div
          v-if="mode === MODES.EXOTIC && legs.some((l) => l.cancelled)"
          class="text-muted mt-50"
          style="font-size: 12px"
        >
          Some runners are voided: payout is recalculated by the server from the
          remaining combos and may differ from this estimate.
        </div>
        <div v-if="hasPaidOut" class="text-danger mt-50" style="font-size: 12px">
          This bet has already been paid. The previous payout will be clawed back
          from the customer's wallet before the new result is applied.
        </div>
      </div>

      <!-- Actions -->
      <div class="d-flex" style="gap: 8px; margin-top: 18px">
        <b-button
          variant="outline-secondary"
          style="flex: 1"
          @click="$refs.modal.hide()"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          style="flex: 2"
          :disabled="!canSave || isSaving"
          @click="handleSave"
        >
          <Spinner v-if="isSaving" />
          <span v-else>Save</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BBadge,
  BFormInput,
  BFormCheckbox,
  BFormRadioGroup,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import Spinner from "@/@core/components/Spinner.vue";
import {
  MANUAL_RESULT_MODES,
  resolveManualResultMode,
  toLegResult,
  toQuaddieResult,
  toBetResult,
  computeLegBetResult,
  computeQuaddieBetResult,
  isValidFinalOdds,
  buildPreviewConfirmMessage,
} from "./utils";

const RESULT_TEXT = {
  win: "Win",
  lose: "Lose",
  draw: "Draw",
  refund: "Refund",
  cancel: "Void",
  pending: "Pending (unresulted)",
};

export default {
  components: {
    BModal,
    BButton,
    BBadge,
    BFormInput,
    BFormCheckbox,
    BFormRadioGroup,
    Spinner,
  },
  data() {
    return {
      MODES: MANUAL_RESULT_MODES,
      bet: null,
      typeLabel: "",
      mode: null,
      legs: [],
      betResult: "pending",
      finalOdds: "",
      legResultOptions: [
        { value: "win", text: "Win" },
        { value: "lose", text: "Lose" },
        { value: "draw", text: "Draw" },
        { value: "cancel", text: "Void" },
        { value: "pending", text: "Pending" },
      ],
      runnerResultOptions: [
        { value: "win", text: "Win" },
        { value: "lose", text: "Lose" },
        { value: "cancel", text: "Void" },
        { value: "pending", text: "Pending" },
      ],
      betResultOptions: [
        { value: "win", text: "Win" },
        { value: "lose", text: "Lose" },
        { value: "refund", text: "Refund" },
        { value: "pending", text: "Pending" },
      ],
      sectionTitleStyle: {
        fontWeight: 700,
        fontSize: "13px",
        paddingBottom: "6px",
        borderBottom: "1px solid #e5e7eb",
        marginBottom: "10px",
      },
    };
  },
  computed: {
    ...mapState("trading/betslip", [
      "loadingManualResultBetslip",
      "loadingPreviewManualResult",
    ]),
    isSaving() {
      return this.loadingManualResultBetslip || this.loadingPreviewManualResult;
    },
    isBonus() {
      return !!this.bet && this.bet.currencyMethod === "bonus";
    },
    // exotic, quaddie, srm and combo-racing pay (stake per combo) x odds per $1
    isPerComboPayout() {
      if (!this.bet) return false;
      if ([this.MODES.EXOTIC, this.MODES.QUADDIE].includes(this.mode))
        return true;
      return ["srm", "combo-racing"].includes(this.bet.type);
    },
    perComboStake() {
      if (!this.bet) return 0;
      if (this.bet.fractionalPercent) return this.bet.fractionalPercent / 100;
      if (this.bet.combos) return (this.bet.stake || 0) / this.bet.combos;
      return this.bet.stake || 0;
    },
    oddsLabel() {
      return this.isPerComboPayout ? "Dividend (per $1)" : "Final Odds";
    },
    oddsHint() {
      return this.isPerComboPayout
        ? "Tote dividend per $1. Payout = stake per combo x dividend."
        : "Final odds of the whole bet (incl. boost/deduction).";
    },
    expectedResult() {
      if (!this.bet) return null;
      if (this.mode === this.MODES.LEG)
        return computeLegBetResult(this.legs.map((l) => l.result));
      if (this.mode === this.MODES.QUADDIE)
        return computeQuaddieBetResult(this.legs);
      return this.betResult;
    },
    isFinalOddsRequired() {
      return ["win", "draw"].includes(this.expectedResult);
    },
    estimatedPayout() {
      if (!this.bet) return 0;
      const stake = Number(this.bet.stake) || 0;
      if (this.expectedResult === "refund") return stake;
      if (!this.isFinalOddsRequired || !isValidFinalOdds(this.finalOdds))
        return 0;
      const base = this.isPerComboPayout ? this.perComboStake : stake;
      const payout = base * Number(this.finalOdds);
      return this.isBonus ? Math.max(payout - stake, 0) : payout;
    },
    estimatedPayoutText() {
      if (this.expectedResult === "pending") return "-";
      if (this.isFinalOddsRequired && !isValidFinalOdds(this.finalOdds))
        return "Enter odds";
      return formatCurrencyBuiltInFunc(this.estimatedPayout);
    },
    hasPaidOut() {
      return (
        !!this.bet &&
        ["resulted", "approved"].includes(this.bet.status) &&
        Number(this.bet.totalPayout) > 0
      );
    },
    changedLegs() {
      return this.legs.filter((l) =>
        this.mode === this.MODES.EXOTIC
          ? this.isPositionChanged(l) || l.cancelled !== l.initialCancelled
          : l.result !== l.initialResult
      );
    },
    canSave() {
      if (!this.bet || !this.mode) return false;
      if (this.isFinalOddsRequired && !isValidFinalOdds(this.finalOdds))
        return false;
      if (
        this.mode === this.MODES.EXOTIC &&
        this.legs.some((l) => this.isInvalidPosition(l.finalPosition))
      )
        return false;
      return true;
    },
  },
  methods: {
    ...mapActions({
      manualResultBetslip: "trading/betslip/manualResultBetslip",
      previewManualResultBetslip: "trading/betslip/previewManualResultBetslip",
    }),
    formatCurrencyBuiltInFunc,
    isValidFinalOdds,
    resultText(result) {
      return RESULT_TEXT[result] || result || "-";
    },
    legRowStyle(idx) {
      const notLast = idx < this.legs.length - 1;
      return {
        paddingBottom: notLast ? "10px" : "0",
        marginBottom: notLast ? "10px" : "16px",
        borderBottom: notLast ? "1px dashed #e5e7eb" : "none",
      };
    },
    // Digits and a single dot only, at most 2 decimals
    formatOddsInput(value) {
      const [int, ...rest] = String(value).replace(/[^\d.]/g, "").split(".");
      return rest.length ? `${int}.${rest.join("").slice(0, 2)}` : int;
    },
    // Unresulting an exotic bet also clears the runners' finish positions
    onBetResultChange(value) {
      if (value !== "pending" || this.mode !== this.MODES.EXOTIC) return;
      this.legs.forEach((l) => {
        l.finalPosition = "";
      });
    },
    // Positive integer only (no leading zeros), empty clears the position
    formatPositionInput(value) {
      return String(value).replace(/\D/g, "").replace(/^0+/, "");
    },
    isPositionChanged(leg) {
      return this.toPosition(leg.finalPosition) !== leg.initialPosition;
    },
    toPosition(value) {
      if (value === null || value === undefined || value === "") return null;
      return Number(value);
    },
    isInvalidPosition(value) {
      const pos = this.toPosition(value);
      return pos !== null && (!Number.isInteger(pos) || pos < 1);
    },
    buildLegLabel(sel) {
      const odds =
        sel.odds != null && sel.odds !== 0
          ? ` @${Number(sel.odds).toFixed(2)}`
          : "";
      if (sel.runnerName || sel.runnerNo) {
        const race = sel.raceNumber
          ? `R${sel.raceNumber}${sel.meetingName ? " " + sel.meetingName : ""} — `
          : "";
        return `${race}${sel.runnerNo ? sel.runnerNo + ". " : ""}${sel.runnerName || ""}${odds}`;
      }
      return (
        [sel.eventTitle, sel.marketName, sel.name].filter(Boolean).join(" — ") +
        odds
      );
    },
    // options.voidLegIds: exotic runners to pre-tick as Void
    openModal(bet, typeLabel = "", options = {}) {
      const mode = resolveManualResultMode(bet);
      if (!mode) {
        this.showToastError("Error", "This bet type does not support manual result.");
        return;
      }
      this.bet = bet;
      this.typeLabel = typeLabel;
      this.mode = mode;
      this.betResult = toBetResult(bet.result);
      this.finalOdds =
        [MANUAL_RESULT_MODES.LEG, MANUAL_RESULT_MODES.SIMPLE].includes(mode) &&
        !["srm", "combo-racing"].includes(bet.type) &&
        bet.odds
          ? String(Number(bet.odds).toFixed(2))
          : "";
      const selections = mode === MANUAL_RESULT_MODES.SIMPLE ? [] : bet.selections || [];
      this.legs = selections.map((sel) => {
        const initialResult =
          mode === MANUAL_RESULT_MODES.QUADDIE
            ? toQuaddieResult(sel.result, sel.status)
            : toLegResult(sel.result, sel.status);
        const initialCancelled = sel.status === "cancelled";
        const initialPosition =
          sel.manualFinalPosition != null ? Number(sel.manualFinalPosition) : null;
        return {
          legId: sel.id,
          label: this.buildLegLabel(sel),
          raceKey: sel.raceId || `${sel.meetingName}-${sel.raceNumber}`,
          // scratched but not yet cancelled by the racing flow (it does so on race result)
          scratched: Number(sel.scratched) === 1 && !initialCancelled,
          initialResult,
          result: initialResult,
          initialCancelled,
          cancelled:
            initialCancelled || (options.voidLegIds || []).includes(sel.id),
          initialPosition,
          finalPosition: initialPosition != null ? String(initialPosition) : "",
        };
      });
      this.$refs.modal.show();
    },
    buildPayload() {
      const payload = { id: this.bet.id };
      if (this.mode === this.MODES.LEG || this.mode === this.MODES.QUADDIE) {
        payload.legs = this.changedLegs.map((l) => ({
          legId: l.legId,
          result: l.result,
        }));
      } else if (this.mode === this.MODES.EXOTIC) {
        payload.mode = "exotic";
        payload.betResult = this.betResult;
        // Only the changed fields: an omitted field is kept as is
        payload.legs = this.changedLegs.map((l) => {
          const leg = { legId: l.legId };
          if (this.isPositionChanged(l))
            leg.finalPosition = this.toPosition(l.finalPosition);
          if (l.cancelled !== l.initialCancelled) leg.cancelled = l.cancelled;
          return leg;
        });
      } else {
        payload.mode = "simple";
        payload.betResult = this.betResult;
      }
      if (this.mode === this.MODES.QUADDIE) payload.mode = "quaddie";
      if (this.isFinalOddsRequired) payload.finalOdds = Number(this.finalOdds);
      return payload;
    },
    async handleSave() {
      if (!this.canSave) return;
      const payload = this.buildPayload();

      // Server-side preview (read-only) drives the confirmation popup
      const preview = await this.previewManualResultBetslip(payload);
      if (!preview) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        buildPreviewConfirmMessage(this.$createElement, preview, {
          betLabel: this.bet.displayId || this.bet.id,
          oddsLabel: this.oddsLabel,
        }),
        {
          title: "Confirm Manual Result",
          okVariant: "danger",
          okTitle: "Confirm",
          cancelTitle: "Cancel",
          centered: true,
        }
      );
      if (!confirmed) return;

      const res = await this.manualResultBetslip(payload);
      if (!res) return;

      const status = res.current && res.current.status;
      const messages = {
        approved: "Result saved. Payout is being processed, reload in a few seconds.",
        active: "Bet moved back to pending.",
        declined: "Bet has been refunded.",
      };
      this.showToastSuccess("Success", messages[status] || "Result saved.");
      this.$emit("success", res);
      this.$refs.modal.hide();
    },
    hideModal() {
      this.bet = null;
      this.typeLabel = "";
      this.mode = null;
      this.legs = [];
      this.betResult = "pending";
      this.finalOdds = "";
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();
    return { showToastSuccess, showToastError };
  },
};
</script>
